import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect if user is on mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load reCAPTCHA script dynamically
  useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (siteKey && !window.grecaptcha) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  // Handle contact actions based on device type
  const handleContactAction = (type) => {
    if (isMobile) {
      // On mobile, use native apps
      if (type === 'phone') {
        window.location.href = 'tel:+919904449922';
      } else if (type === 'email') {
        window.location.href = 'mailto:nitumf2017@gmail.com';
      }
    } else {
      // On desktop, scroll to contact form
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Validation functions
  const validateName = (name) => {
    if (!name.trim()) return "Full name is required";
    if (name.trim().length < 2) return "Name must be at least 2 characters";
    if (name.trim().length > 50) return "Name must be less than 50 characters";
    if (!/^[a-zA-Z\s]+$/.test(name.trim())) return "Name can only contain letters and spaces";
    return null;
  };

  const validateEmail = (email) => {
    if (!email.trim()) return "Email address is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email address";
    if (email.length > 100) return "Email address is too long";
    return null;
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) return "Phone number is required";
    // Allow international phone numbers with optional country code
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    const phoneRegex = /^(\+?\d{1,4})?[\d]{7,15}$/;
    if (!phoneRegex.test(cleanPhone)) {
      return "Please enter a valid phone number";
    }
    if (cleanPhone.length > 20) return "Phone number is too long";
    return null;
  };

  const validateMessage = (message) => {
    if (!message.trim()) return "Message is required";
    if (message.trim().length < 10) return "Message must be at least 10 characters";
    if (message.trim().length > 1000) return "Message must be less than 1000 characters";
    return null;
  };

  const validateForm = () => {
    const newErrors = {};

    const nameError = validateName(formData.name);
    if (nameError) newErrors.name = nameError;

    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    const phoneError = validatePhone(formData.phone);
    if (phoneError) newErrors.phone = phoneError;

    const messageError = validateMessage(formData.message);
    if (messageError) newErrors.message = messageError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Get reCAPTCHA token
      let recaptchaToken = null;
      const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

      if (siteKey && window.grecaptcha && window.grecaptcha.ready) {
        try {
          // Wait for reCAPTCHA to be ready
          await new Promise((resolve) => window.grecaptcha.ready(resolve));
          recaptchaToken = await window.grecaptcha.execute(siteKey, { action: 'contact_form' });
          console.log('reCAPTCHA token obtained');
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError);
          // Continue without reCAPTCHA token - server will decide if required
        }
      } else {
        console.log('reCAPTCHA not available - siteKey:', !!siteKey, 'grecaptcha:', !!window.grecaptcha);
      }

      // Use Vercel serverless API (same domain, no CORS issues)
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Form submitted successfully:', result);
        setIsSubmitted(true);

        // Reset form and errors after 3 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setIsSubmitting(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            service: '',
            message: ''
          });
          setErrors({});
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Form submission failed:', response.statusText);
        alert(errorData.detail || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to send message. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone size={24} />,
      title: "Phone",
      details: (
        <div className="space-y-2">
          <div>
            <div className="font-semibold text-primary-green">Proprietor</div>
            {isMobile ? (
              <a href="tel:+919904449922" className="hover:text-primary-gold transition-colors">
                +91 99044 49922
              </a>
            ) : (
              <span>+91 99044 49922</span>
            )}
          </div>
          <div className="pt-2 border-t border-gray-200">
            <div className="font-semibold text-primary-green">Back Office</div>
            {isMobile ? (
              <a href="tel:+918200076061" className="hover:text-primary-gold transition-colors">
                +91 820-0076061
              </a>
            ) : (
              <span>+91 820-0076061</span>
            )}
            <div className="text-sm text-gray-600 mt-1">
              Relationship Managers: Pooja Shah & Dev Upadhyay
            </div>
          </div>
        </div>
      ),
      action: null
    },
    {
      icon: <Mail size={24} />,
      title: "Email",
      details: "nitumf2017@gmail.com",
      action: "email"
    },
    {
      icon: <MapPin size={24} />,
      title: "Location",
      details: "426- shivalik shilp iscon char cross road  , Ahmedabad - 380015",
      action: null
    },
    {
      icon: <Clock size={24} />,
      title: "Business Hours",
      details: (
        <div className="space-y-1">
          <div>Mon – Fri: 10:00 AM – 6:00 PM</div>
          <div>Sat: 10:00 AM – 2:00 PM</div>
          <div>
            Sun: <span className="text-red-600 font-semibold">Closed</span>
          </div>
        </div>
      ),
      action: null
    }


  ];

  const services = [
    "Mutual Fund Investing",
    "Fixed Deposits",
    "Financial Planning",
    "Insurance Services",
    "General Inquiry"
  ];

  return (
    <section id="contact" className="section-padding bg-gradient-section">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="heading-section fade-in-up">Get In Touch</h2>
          <p className="text-large max-w-2xl mx-auto fade-in-up delay-1">
            Ready to take the next step? Reach out to NSA Financial Services and let's discuss your financial goals.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="fade-in-up delay-2">
            <div className="bg-white rounded-3xl p-8 shadow-xl">
              <h3 className="heading-card mb-6">Send us a Message</h3>

              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block font-medium text-primary-green mb-2">
                        Full Name * <span className="text-sm text-gray-500">(2-50 characters)</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        maxLength={50}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-gold focus:border-transparent transition-all duration-300 ${errors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Your full name"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="phone" className="block font-medium text-primary-green mb-2">
                        Phone Number * <span className="text-sm text-gray-500">(with country code)</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        maxLength={20}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-gold focus:border-transparent transition-all duration-300 ${errors.phone ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                        placeholder="+1 234 567 8900"
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block font-medium text-primary-green mb-2">
                      Email Address * <span className="text-sm text-gray-500">(max 100 characters)</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      maxLength={100}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-gold focus:border-transparent transition-all duration-300 ${errors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                        }`}
                      placeholder="your.email@example.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="service" className="block font-medium text-primary-green mb-2">
                      Service Interest
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-gold focus:border-transparent transition-all duration-300"
                    >
                      <option value="">Select a service</option>
                      {services.map((service, index) => (
                        <option key={index} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block font-medium text-primary-green mb-2">
                      Message * <span className="text-sm text-gray-500">(10-1000 characters)</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows="5"
                      maxLength={1000}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-gold focus:border-transparent transition-all duration-300 resize-none ${errors.message ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Tell us about your financial goals or questions..."
                    ></textarea>
                    {errors.message && (
                      <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                    )}
                    <div className="text-right text-sm text-gray-500 mt-1">
                      {formData.message.length}/1000 characters
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`btn-primary w-full ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              ) : (
                <div className="text-center py-12">
                  <div className="text-green-500 mb-4">
                    <CheckCircle size={64} className="mx-auto" />
                  </div>
                  <h3 className="heading-card text-green-600 mb-4">
                    Message Sent Successfully!
                  </h3>
                  <p className="text-body">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="fade-in-up delay-3">
            <div className="space-y-8">
              <div>
                <h3 className="heading-card mb-6"></h3>
                <div className="grid gap-3">
                  {contactInfo.map((info, index) => (
                    <div
                      key={index}
                      className="contact-info flex items-start gap-4 p-1 rounded-2xl hover:shadow-lg transition-all duration-300"
                    >
                      <div className="text-primary-gold flex-shrink-0">
                        {info.icon}
                      </div>
                      <div>
                        <h4 className="font-poppins font-semibold text-primary-green mb-2">
                          {info.title}
                        </h4>
                        {info.action ? (
                          <button
                            onClick={() => handleContactAction(info.action)}
                            className="text-body hover:text-primary-gold transition-colors duration-300 text-left bg-transparent border-none cursor-pointer p-0"
                          >
                            {info.details}
                          </button>
                        ) : (
                          <p className="text-body">{info.details}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Contact Actions & Business Hours */}
              <div className="bg-primary-green rounded-2xl p-5 text-white">
                <h4 className="font-poppins font-semibold mb-4">
                  Need Immediate Assistance?
                </h4>
                <p className="text-sm opacity-90 mb-3">
                  For urgent financial queries or investment opportunities, reach out directly.
                </p>
                <div className="flex flex-col sm:flex-row ">
                  <button
                    onClick={() => handleContactAction('phone')}
                    className="btn-primary bg-primary-gold text-primary-green hover:bg-yellow-400 flex-1 justify-center"
                  >
                    <Phone size={18} />
                    {isMobile ? 'Call Now' : 'Contact Us'}
                  </button>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;